import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Trash2, MapPin, Phone, Mail, Calendar, User } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import api from '../../utils/api';

const STATUS_OPTIONS = ['未対応', '対応中', '完了', 'キャンセル'];

const TourReservationManagement = ({ onUpdate }) => {
  const [reservations, setReservations] = useState([]);
  const { toast } = useToast();

  const fetchReservations = async () => {
    try {
      const res = await api.get('/tour-reservations');
      setReservations(res.data);
    } catch (e) {
      console.error('見学予約の取得に失敗しました:', e);
    }
  };

  useEffect(() => {
    fetchReservations();
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/tour-reservations/${id}`, { status });
      fetchReservations();
      if (onUpdate) onUpdate();
    } catch (e) {
      toast({ title: 'エラー', description: 'ステータス更新に失敗しました', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('この見学予約を削除しますか？')) return;
    try {
      await api.delete(`/tour-reservations/${id}`);
      fetchReservations();
      if (onUpdate) onUpdate();
      toast({ title: '削除しました', description: '見学予約を削除しました' });
    } catch (e) {
      toast({ title: 'エラー', description: '削除に失敗しました', variant: 'destructive' });
    }
  };

  return (
    <Card className="bg-gradient-to-br from-gray-50/80 to-gray-100/80 border-amber-500/30">
      <CardHeader>
        <CardTitle className="text-amber-500">見学予約管理</CardTitle>
      </CardHeader>
      <CardContent>
        {reservations.length === 0 ? (
          <p className="text-center text-gray-500 py-8" data-testid="tour-empty">見学予約はありません。</p>
        ) : (
          <div className="space-y-4">
            {reservations.map((r) => (
              <div key={r.id} className="p-4 rounded-lg border border-amber-500/20 bg-white/50" data-testid={`tour-item-${r.id}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-amber-500" />
                    <span className="font-semibold text-gray-900">{r.office_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={r.status || '未対応'} onValueChange={(v) => updateStatus(r.id, v)}>
                      <SelectTrigger className="h-8 w-32 bg-white border-amber-500/30 text-gray-900 text-xs" data-testid={`tour-status-${r.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="icon" onClick={() => handleDelete(r.id)} className="h-8 w-8 border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid={`tour-delete-${r.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-2 text-sm text-gray-600">
                  <div className="flex items-center gap-2"><User className="w-4 h-4 text-amber-500" />{r.name}</div>
                  <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-amber-500" />{r.phone}</div>
                  {r.email && <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-amber-500" />{r.email}</div>}
                  {r.preferred_date && <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-amber-500" />{r.preferred_date}</div>}
                </div>
                {r.message && <p className="mt-2 text-sm text-gray-600 whitespace-pre-line bg-amber-50/50 rounded p-2">{r.message}</p>}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TourReservationManagement;
